import React, { useState } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { AddCircle as AddCircleIcon } from '@mui/icons-material';

import CustomSortableTable, { SortableTableAction, SortableTableHeader, SortableTableRow, SortableTableFilter } from '../../../../components/CustomSortableTable';

import { AreaItem } from '../Common';

import styles from './styles';
import { booleanToYesNo } from '../../../../utils/stringUtils';
import CustomDialog from '../../../CustomDialog';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  classes: ClassNameMap<string>;
  items: AreaItem[];
  handleCreate: () => void;
  handleView: (id: string) => void;
  handleEdit: (id: string, view: string) => void;
  handleActiveStatusChange: (id: string, isActive: boolean) => Promise<void>;
}

const ListAreas = ({ classes, history, items, handleCreate, handleView, handleEdit, handleActiveStatusChange }: Props): React.ReactElement => {
  const [activeFiltered, setActiveFiltered] = useState<boolean>(true);
  const [recordStatusState, setRecordStatusState] = useState<{
    dialogOpen: boolean;
    areaId: string;
    isActive: boolean;
    isLoading: boolean;
  }>({
    dialogOpen: false,
    areaId: '',
    isActive: false,
    isLoading: false,
  });

  const deactivateArea = async () => {
    setRecordStatusState((prev) => ({ ...prev, isLoading: true }));
    await handleActiveStatusChange(recordStatusState.areaId, recordStatusState.isActive);
    setRecordStatusState((prev) => ({ ...prev, isLoading: false, dialogOpen: false }));
  };

  const headers: SortableTableHeader[] = [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'sections', label: 'Wards', align: 'center' },
    { key: 'grouping', label: 'Grouping', align: 'center' },
    { key: 'users', label: 'Users', align: 'center' },
    { key: 'active', label: 'Active' },
  ];

  const rows: SortableTableRow[] = items.map((item: AreaItem) => ({
    key: item.id,
    actions: [
      {
        label: item.active ? 'Deactivate' : 'Activate',
        destructive: item.active,
        onClick: () => setRecordStatusState((prev) => ({ ...prev, areaId: item.id, isActive: !item.active, dialogOpen: true })),
      },
      {
        label: 'View',
        onClick: () => handleView(item.id),
      },
    ],
    columns: [
      {
        key: 'name',
        label: item.name,
      },
      {
        key: 'phone',
        label: item.phone,
      },
      {
        key: 'sections',
        label: item.sections.length,
      },
      {
        key: 'grouping',
        label: item.category_assignments.length,
      },
      {
        key: 'users',
        label: item.users_aggregate.aggregate.count,
      },
      {
        key: 'active',
        label: booleanToYesNo(item.active),
      },
    ],
    collapsible: [
      {
        title: 'Wards',
        counter: item.sections.length,
        headers: [
          { key: 'name', label: 'Name' },
          { key: 'phone', label: 'Phone' },
        ],
        rows: item.sections.map((i) => ({
          key: i.id,
          actions: [{ label: 'Edit', onClick: () => handleEdit(i.id, `areas/${item.id}/wards`) }],
          columns: [
            { key: 'name', label: i.name },
            { key: 'phone', label: i.phone },
          ],
        })),
      },
      {
        title: 'Grouping',
        counter: item.category_assignments.length,
        headers: [
          { key: 'name', label: 'Name' },
          { key: 'description', label: 'Description' },
        ],
        rows: item.category_assignments
          .map((i) => i.category)
          .map((i) => ({
            key: i.id,
            actions: [{ label: 'Edit', onClick: () => handleEdit(i.id, 'grouping') }],
            columns: [
              { key: 'name', label: i.name },
              { key: 'description', label: i.description },
            ],
          })),
      },
    ].filter((i) => i.rows.length > 0),
  }));

  const actions: SortableTableAction[] = [
    {
      key: 'create',
      label: 'Create Department',
      icon: <AddCircleIcon />,
      onClick: () => handleCreate(),
    },
  ];

  const activeFilter: SortableTableFilter = {
    key: 'activeFilter',
    label: 'Active',
    filter: (rows: SortableTableRow[]) =>
      rows.filter((r) => {
        const col = r.columns.find((c) => c.key === 'active');
        if (col) {
          return activeFiltered ? col.label === 'Yes' : col.label === 'No';
        }
        return true;
      }),
    component: (
      <ToggleButtonGroup className={classes.activeFilter} size="medium" value={[activeFiltered]} color="primary" onChange={() => setActiveFiltered(!activeFiltered)}>
        <ToggleButton className={classes.toggleButton} key="active" value={true}>
          Active
        </ToggleButton>
        <ToggleButton className={classes.toggleButton} key="inactive" value={false}>
          Inactive
        </ToggleButton>
      </ToggleButtonGroup>
    ),
  };

  const filters = [activeFilter];

  const filterRows = (): SortableTableRow[] => {
    let filteredRows: SortableTableRow[] = rows;
    filters.forEach((f) => {
      filteredRows = f.filter(filteredRows);
    });
    return filteredRows;
  };

  return (
    <>
      <Grid container spacing={4} alignItems="stretch">
        <Grid item xs={12}>
          <CustomSortableTable title="Departments" orderdBy="name" ordered="asc" actions={actions} headers={headers} counter={rows.length} rows={filterRows()} filters={filters} />
        </Grid>
      </Grid>
      <CustomDialog
        open={recordStatusState.dialogOpen}
        title="Update Area"
        message={`Are you sure you want to ${recordStatusState.isActive ? 'activate' : 'deactivate'} this area?`}
        handleClose={() => setRecordStatusState((prev) => ({ ...prev, dialogOpen: false }))}
        actions={[
          { label: 'No', onClick: () => setRecordStatusState((prev) => ({ ...prev, dialogOpen: false })) },
          { label: 'Yes', onClick: () => deactivateArea() },
        ]}
      />
    </>
  );
};

export default withRouter(withStyles(styles)(ListAreas));
